import { AriValues, DailySet, Promo, Row } from "./types";
import {
  addDays,
  eachDayOfInterval,
  format,
  isWithinInterval,
  startOfDay,
  subDays,
} from "date-fns";

function mapToInterval(dailySet?: DailySet[]) {
  if (Array.isArray(dailySet)) {
    return dailySet.map((it: DailySet) => ({
      range: {
        start: startOfDay(new Date(it.startDate)),
        end: startOfDay(new Date(it.endDate)),
      },
      value: it.value,
    }));
  }
}

export function generateRows({
  ariValues,
  selectedInventoryTicker,
  sortedPromos,
  checkIn,
  checkOut,
  initial,
  currentGhostDate,
}: {
  selectedInventoryTicker: string;
  sortedPromos: Promo[];
  ariValues: AriValues | undefined;
  checkIn: string;
  checkOut: string;
  initial?: boolean;
  currentGhostDate?: Date;
}) {
  const items: Row[] = [];
  const parsedCheckIn = startOfDay(new Date(checkIn));
  const parsedCheckOut = startOfDay(new Date(checkOut));

  if (checkIn && checkOut && selectedInventoryTicker) {
    const mainRange = {
      start: parsedCheckIn,
      end: subDays(parsedCheckOut, 1),
    };

    // A past reservation may have no ARI left for its dates, and its composite inventory
    // ticker may no longer exist in the property configuration. In either case the response
    // carries no rate for it, so we fall back to an empty set: the rows are still built (the
    // caller fills them from the reservation's own stored daily values) and the loaded rate
    // renders as "-".
    const inventoryAri = ariValues?.[selectedInventoryTicker];

    const dailyAriPrice: Record<string, number> =
      inventoryAri?.rate?.dailySet
        ?.flatMap(range => {
          return eachDayOfInterval({
            start: startOfDay(new Date(range.startDate)),
            end: startOfDay(new Date(range.endDate)),
          }).map(date => ({
            [format(date, "yyyy-MM-dd")]: Number(Number(range.value).toFixed(2)),
          }));
        })
        .reduce((accum, curr) => ({ ...accum, ...curr }), {}) ?? {};

    if (initial) {
      const days = eachDayOfInterval({
        start: subDays(parsedCheckIn, 3),
        end: addDays(parsedCheckOut, 2),
      }).map(day => format(day, "yyyy-MM-dd"));
      days.forEach((day, index) => {
        const ariPrice =
          dailyAriPrice[day] ||
          (dailyAriPrice[day] === undefined || dailyAriPrice[day] === null ? "-" : 0);
        items.push({
          date: day,
          ariPrice,
          promos: sortedPromos,
          appliedPrice: undefined,
        });
      });
    } else if (!initial && currentGhostDate) {
      const ariPrice =
        dailyAriPrice[format(currentGhostDate, "yyyy-MM-dd")] ||
        (dailyAriPrice[format(currentGhostDate, "yyyy-MM-dd")] === undefined ||
        dailyAriPrice[format(currentGhostDate, "yyyy-MM-dd")] === null
          ? "-"
          : 0);
      items.push({
        date: format(currentGhostDate, "yyyy-MM-dd"),
        ariPrice,
        promos: sortedPromos,
        appliedPrice: undefined,
      });
    }

    const availabilityRange = mapToInterval(inventoryAri?.availability?.dailySet);
    const closedRange = mapToInterval(inventoryAri?.closed?.dailySet);
    const maxNoticeRange = mapToInterval(inventoryAri?.maxNotice?.dailySet);
    const minNoticeRange = mapToInterval(inventoryAri?.minNotice?.dailySet);
    const minStayRange = mapToInterval(inventoryAri?.minStay?.dailySet);
    const maxStayRange = mapToInterval(inventoryAri?.maxStay?.dailySet);
    items.map(item => {
      const itemDate = startOfDay(new Date(item.date));
      const availability =
        availabilityRange &&
        availabilityRange.find(it => isWithinInterval(itemDate, it.range))?.value;
      const closed =
        closedRange && closedRange.find(it => isWithinInterval(itemDate, it.range))?.value;
      const maxNotice =
        maxNoticeRange && maxNoticeRange.find(it => isWithinInterval(itemDate, it.range))?.value;
      const minNotice =
        minNoticeRange && minNoticeRange.find(it => isWithinInterval(itemDate, it.range))?.value;
      const minStay =
        minStayRange && minStayRange.find(it => isWithinInterval(itemDate, it.range))?.value;
      const maxStay =
        maxStayRange && maxStayRange.find(it => isWithinInterval(itemDate, it.range))?.value;
      item.restrictions = {};

      if (availability) {
        item.restrictions.availability = Number(availability);
      }
      if (closed) {
        item.restrictions.closed = Boolean(closed);
      }
      if (maxNotice) {
        item.restrictions.maxNotice = Number(maxNotice);
      }
      if (minNotice) {
        item.restrictions.minNotice = Number(minNotice);
      }
      if (minStay) {
        item.restrictions.minStay = Number(minStay);
      }
      if (maxStay) {
        item.restrictions.maxStay = Number(maxStay);
      }

      if (isWithinInterval(startOfDay(itemDate), mainRange)) {
        item.appliedPrice = 0;
      }

      return item;
    });
  }
  return items;
}
